/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spin, Typography } from 'antd';
import mapboxgl from 'mapbox-gl';

import { CloseOutlined, EnvironmentOutlined } from '@ant-design/icons';

import MapControls from './MapControls';
import {
  clearMapData,
  drawTrackingData,
  fetchFormattedEta,
  fitMapToData,
  initializeMap,
  stopRouteAnimation,
  updateSelectedLocationMarker,
} from './mapUtils';
import OperatorSelector from './OperatorSelector';

import 'mapbox-gl/dist/mapbox-gl.css';
import './CustomPopup.scss';

const { Text } = Typography;

const configureMapboxToken = (settings) => {
  if (settings?.mapBoxToken && !mapboxgl.accessToken) {
    mapboxgl.accessToken = settings.mapBoxToken;
  }
};

// Modal de tela cheia para o mapa
function MapFullScreen({
  isOpen,
  onClose,
  task,
  trackingData,
  selectedOperator,
  selectedLocation,
  onLocationSelect,
  availableOperators,
  onOperatorChange,
  loading,
  settings,
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const selectedLocationMarker = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [pointsToBound, setPointsToBound] = useState([]);
  const [eta, setEta] = useState(null);
  const [isFollowingOperator, setIsFollowingOperator] = useState(false);

  const clear = () => clearMapData(map.current, markers);

  useEffect(() => {
    if (isOpen && !map.current) {
      configureMapboxToken(settings);

      const newMap = initializeMap(mapContainer, setIsMapLoaded);
      if (newMap) {
        map.current = newMap;
        newMap.on('dragstart', () => setIsFollowingOperator(false));
      }
    }

    if (!isOpen) {
      stopRouteAnimation(map.current);
    }
  }, [isOpen, settings]);

  // Atualiza o mapa com os dados e ajusta o zoom
  useEffect(() => {
    if (isOpen && isMapLoaded) {
      clear();
      drawTrackingData(map.current, trackingData, markers, task, selectedOperator).then(
        (newPoints) => {
          setPointsToBound(newPoints);
          fitMapToData(map.current, trackingData, task);
          // ETA
          (async () => {
            const value = await fetchFormattedEta(selectedOperator, trackingData, task);
            setEta(value);
          })();
        }
      );
    }
  }, [isOpen, isMapLoaded, trackingData, selectedOperator, task]);

  // Efeito para seguir o operador se o modo estiver ativo
  useEffect(() => {
    if (isFollowingOperator && trackingData?.length) {
      const sorted = [...trackingData].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      const lastPoint = sorted[sorted.length - 1];
      map.current?.flyTo({ center: [lastPoint.longitude, lastPoint.latitude], zoom: 17 });
    }
  }, [isFollowingOperator, trackingData]);

  // Gerencia o marcador de localização selecionada
  useEffect(() => {
    if (selectedLocationMarker.current) {
      selectedLocationMarker.current.remove();
      selectedLocationMarker.current = null;
    }

    if (isOpen && map.current && selectedLocation) {
      updateSelectedLocationMarker(map.current, selectedLocation, selectedLocationMarker);
    }
  }, [isOpen, selectedLocation]);

  const handleCenterOnStart = () => {
    if (!map.current || !trackingData?.length) return;
    const sorted = [...trackingData].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    map.current.flyTo({ center: [sorted[0].longitude, sorted[0].latitude], zoom: 17 });
  };

  const handleCenterOnDestination = () => {
    if (!map.current) return;
    const destLat = parseFloat(task?.latitude);
    const destLng = parseFloat(task?.longitude);
    if (Number.isNaN(destLat) || Number.isNaN(destLng)) return;
    map.current.flyTo({ center: [destLng, destLat], zoom: 17 });
  };

  const handleCenterOnOperator = () => {
    const nextFollowingState = !isFollowingOperator;
    setIsFollowingOperator(nextFollowingState);

    if (nextFollowingState && map.current && trackingData?.length) {
      if (selectedOperator?.trackingEnd) {
        setIsFollowingOperator(false);
        return;
      }
      const sorted = [...trackingData].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      const operatorPoint = sorted[sorted.length - 1]; // Última posição do operador
      map.current.flyTo({ center: [operatorPoint.longitude, operatorPoint.latitude], zoom: 17 });
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={() => {
        stopRouteAnimation(map.current);
        onClose();
      }}
      title={`Percurso: ${selectedOperator?.name || 'Operador'}`}
      width="95vw"
      height="95vh"
      style={{ top: 20 }}
      footer={null}
      destroyOnClose
      className="map-fullscreen-modal"
    >
      <div className="map-fullscreen-content">
        <div className="map-fullscreen-controls">
          <OperatorSelector
            operators={availableOperators}
            selectedOperator={selectedOperator}
            onOperatorChange={onOperatorChange}
            loading={loading}
          />
          <MapControls
            onCenter={handleCenterOnStart}
            onCenterDestination={handleCenterOnDestination}
            onCenterOperator={handleCenterOnOperator}
            onFit={() => fitMapToData(map.current, trackingData, task)}
            onToggleFullScreen={onClose}
            fitDisabled={pointsToBound.length === 0}
            routeDisabled={!trackingData?.length}
            selectedOperator={selectedOperator}
            isFullScreen
            eta={eta}
            isFollowingOperator={isFollowingOperator}
          />
        </div>
        <div className={`map-fullscreen-container ${!trackingData?.length ? 'disabled' : ''}`}>
          {selectedLocation && (
            <div className="selected-location-indicator">
              <Text>
                Lat: {selectedLocation.lat.toFixed(6)}, Lng: {selectedLocation.lng.toFixed(6)}
              </Text>
              <Button
                size="small"
                type="text"
                icon={<CloseOutlined />}
                onClick={() => onLocationSelect(null)}
              />
            </div>
          )}

          {selectedOperator?.trackingStart && !selectedOperator?.trackingEnd && (
            <div className="route-status-badge">
              <EnvironmentOutlined /> EM PERCURSO
            </div>
          )}
          <div ref={mapContainer} className="map-fullscreen" />
          {!isMapLoaded && (
            <div className="map-fullscreen-loading">
              <Spin size="large" />
              <Text>Carregando mapa...</Text>
            </div>
          )}

          {!loading && isMapLoaded && !trackingData?.length && (
            <div className="map-fullscreen-no-data">
              <span>Nenhum dado de rastreamento para exibir</span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default MapFullScreen;
